
import type { LucideProps } from 'lucide-react';
import type { ForwardRefExoticComponent, RefAttributes } from 'react';
import type { Timestamp } from 'firebase/firestore';

export type LucideIcon = ForwardRefExoticComponent<Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>>;

export function generateId(prefix: string) {
  // Sanitize prefix to only contain alphanumeric characters, hyphens, and underscores
  const sanitizedPrefix = prefix.replace(/[^a-zA-Z0-9\-_]/g, '-');
  return `${sanitizedPrefix}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

export type UserProfile = {
  email: string;
  displayName?: string;
  fullName?: string;
  passwordLastUpdated?: string; // ISO timestamp when password was last changed
  requiresPasswordUpdate?: boolean; // Flag to force password update on next login
  notificationPreferences?: {
    dueDateNotifications?: {
      enabled: boolean;
      notifyOnDueDate: boolean;   // Notify when task becomes due
      notifyWhenOverdue: boolean;  // Notify when task is overdue
    };
    mentionNotifications?: {
      enabled: boolean; // For future - currently always enabled
    };
  };
}

// Formula types for formula columns
export type FormulaOperator = 'add' | 'subtract' | 'multiply' | 'divide';

export type FormulaCondition = {
  columnId: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'greater_than' | 'less_than';
  value: string | number;
};

export type FormulaExpression = {
  type: 'operation' | 'column' | 'constant';
  operator?: FormulaOperator;
  columnId?: string;
  value?: number;
  left?: FormulaExpression;
  right?: FormulaExpression;
};

export type FormulaElseValue = {
  type: 'constant' | 'column' | 'operation';
  value?: number; // for constant
  columnId?: string; // for column
  expression?: FormulaExpression; // for operation
};

export type FormulaDefinition = {
  expression: FormulaExpression;
  returnType?: 'number' | 'currency'; // How to display the result
  condition?: FormulaCondition; // Optional: only calculate if condition is met
  elseValue?: FormulaElseValue; // Optional: value to use when condition is not met
};

export type Column = {
  id: string;
  title: string;
  type: 'text' | 'date' | 'time' | 'person' | 'status' | 'labels' | 'email' | 'website' | 'phone' | 'number' | 'currency' | 'budget' | 'actual' | 'formula';
  icon?: LucideIcon;
  isCore?: boolean;
  width?: number;
  includeInSubtasks?: boolean;
  enabled?: boolean; // defaults to true
  isDueDate?: boolean; // for date columns - marks if this should be used for overdue logic
  includeTime?: boolean; // for date columns - allows users to optionally store a time as well
  showInCardView?: boolean; // for card view - which fields to display on cards (defaults to true)
  formula?: FormulaDefinition; // for formula columns
  defaultValue?: any; // Default value for new tasks - type varies by column type (string, number, string[], etc.)
                      // Special dynamic values:
                      // - Person columns: array can include '__CURRENT_USER__' which resolves to the logged-in user
                      // - Date columns: '__TODAY__', '__TODAY_PLUS_1__', '__TODAY_PLUS_7__', '__TODAY_PLUS_30__' for relative dates
};

export type StatusOption = {
  value: string;
  label: string;
  color: string;
  isDone?: boolean; // Marks if this status should be considered "done" for calculations
};

export type User = {
  id: string;
  name: string;
  email?: string;
  color?: string;
};

export type UserPresence = {
  userId: string;
  userName: string;
  userColor?: string;
  lastSeen: number;
  editingTaskId?: string; // ID of task currently being edited
};

export type FileAttachment = {
  id: string;
  name: string;
  size: number;
  type: string;
  url: string;
  uploadedBy: User;
  uploadedAt: string;
  commentId?: string; // Links to comment if attached there
};

export type Comment = {
  id: string;
  user: User;
  timestamp: string;
  content: string;
  attachments?: FileAttachment[];
  taggedUsers?: string[]; // Array of user IDs that are tagged in this comment
};

export type Activity = {
  id: string;
  user: User;
  timestamp: string;
  type: 'comment' | 'update' | 'create';
  content?: string; // for comments
  attachments?: FileAttachment[]; // for comments
  taggedUsers?: string[]; // for comments - tagged user IDs
  field?: string; // for updates and create
  oldValue?: any;
  newValue?: any;
};

export type Task = {
  id:string;
  title: string;
  [key: string]: any;
  comments: Comment[];
  activities?: Activity[];
  budget?: number;
  actual?: number;
  subtasks?: Task[];
  description?: string;
  files?: FileAttachment[];
};

export type Section = {
  id: string;
  title: string;
  icon: string;
  tasks: Task[];
};

export type WidgetLayoutPlatform = 'web' | 'iosRegular';
export type WidgetLayoutSurface = 'taskPage' | 'dashboard';

export type WidgetLayoutProfile = {
  gridSpan?: 1 | 2 | 3;
  gridHeight?: 1 | 2 | 3;
  layoutX?: number;
  layoutY?: number;
  layoutW?: number;
  layoutH?: number;
};

export type WidgetLayoutProfiles = {
  web?: Partial<Record<WidgetLayoutSurface, WidgetLayoutProfile>>;
  iosRegular?: Partial<Record<WidgetLayoutSurface, WidgetLayoutProfile>>;
};

// Represents a configured widget instance in a project
export type Widget = {
  id: string;
  title: string;
  type: 'predefined' | 'custom';
  definitionId?: string; // e.g., 'budget', 'workload' for predefined
  customConfig?: CustomWidgetConfig;
  target?: number;
  ignoreUnassigned?: boolean;

  // Grid sizing – number of columns this widget spans (1–3, default 1)
  gridSpan?: 1 | 2 | 3;
  gridHeight?: 1 | 2 | 3;
  layoutX?: number;
  layoutY?: number;
  layoutW?: number;
  layoutH?: number;
  layoutProfiles?: WidgetLayoutProfiles;

  // New properties for per-screen configuration
  taskPageOrder?: number;
  dashboardTabOrder?: number;
  taskPageEnabled?: boolean;
  dashboardTabEnabled?: boolean;

  // Deprecated properties - will be migrated
  enabled?: boolean;
  displayLocation?: 'task-page' | 'dashboard-tab' | 'everywhere';
};

// Stored configuration for a user-created custom widget
export type CustomWidgetConfig = {
  title: string;
  widgetTypeId: string; // Links to a WidgetTypeDefinition
  primaryColumnId?: string;
  secondaryColumnId?: string;
  selectedColumnIds?: string[]; // For widgets that need multiple columns
  ignoreUnassigned?: boolean;
  visualization?: 'bar' | 'pie' | 'doughnut' | 'line' | 'table';
  aggregation?: 'sum' | 'count' | 'average' | 'percentage';
};

// Static definition for a type of custom chart that can be created
export type ChartTypeDefinition = {
  id: string;
  name: string;
  description: string;
  compatiblePrimaryColumnTypes: Column['type'][];
  compatibleSecondaryColumnTypes?: Column['type'][];
  requiresSecondaryColumn?: boolean;
  visualization: 'bar' | 'pie' | 'doughnut' | 'line' | 'table';
};

// =====================================================================
// ChartCard (charts v2) — greenfield schema. Behind charts_v2 flag.
// Coexists with Widget during the dual-read rollout window.
// =====================================================================

export type ChartCardKind =
  | 'kpi'
  | 'progress'
  | 'breakdown'
  | 'distribution'
  | 'comparison'
  | 'trend'
  | 'matrix'
  | 'leaderboard'
  | 'metricsTable';

export type ChartCardMetric = 'count' | 'sum' | 'avg' | 'progress' | 'budget';

export type ChartCardScope = { kind: 'project' };

export type ChartCardTimeWindow =
  | { preset: '7d' | '30d' | '90d' | 'qtd' | 'ytd' | 'all' }
  | { custom: { startISO: string; endISO: string } };

export type ChartCardStyle = 'donut' | 'bar' | 'line' | 'area' | 'ring';

export type ChartCardSurfaces = {
  inline: { enabled: boolean; order: number };
  page: { enabled: boolean; order: number };
};

export type ChartCardSource = {
  metric: ChartCardMetric;
  valueColumnId?: string;
  valueColumnIds?: string[];
  groupByColumnId?: string;
  secondaryGroupByColumnId?: string;
  timeColumnId?: string;
  bucket?: 'day' | 'week' | 'month';
  window?: ChartCardTimeWindow;
};

export type ChartCardFilter = {
  ignoreUnassigned?: boolean;
  statuses?: string[];
  assignees?: string[];
};

export type ChartCardComparison = {
  against: 'previous_period' | 'target';
  target?: number;
};

export type ChartCardDisplay = {
  title: string;
  subtitle?: string;
  accentToken?: string;
  style?: ChartCardStyle;
  showLegend?: boolean;
  showSparkline?: boolean;
};

export type ChartCardInteraction = {
  onSelect?: 'filterBoard' | 'none';
};

export type ChartCard = {
  id: string;
  schemaVersion: 2;
  scope: ChartCardScope;
  surfaces: ChartCardSurfaces;
  kind: ChartCardKind;
  source: ChartCardSource;
  filter?: ChartCardFilter;
  comparison?: ChartCardComparison;
  display: ChartCardDisplay;
  interaction?: ChartCardInteraction;
};


export type GlobalSort = {
  columnId: string;
  direction?: 'asc' | 'desc';
} | null;

export type SectionMetric = 'progress' | 'budget' | 'actual' | 'taskCount';
export type ProjectSnapshotReason = 'destructive_write' | 'scheduled' | 'manual' | 'before_restore';

export type ProjectSnapshotSummary = {
  id: string;
  projectId: string;
  projectTitle: string;
  projectIcon?: string | null;
  reason: ProjectSnapshotReason;
  createdAt: string | null;
  createdBy?: string | null;
  version: number;
  taskCounts: { tasks: number };
  subtaskCount: number;
  sourceSnapshotId?: string | null;
};

export type ProjectWriteGuardReason =
  | 'task-delete'
  | 'bulk-delete'
  | 'section-delete'
  | 'quick-add-cancel';

export type ProjectWriteGuard = {
  reason: ProjectWriteGuardReason;
  expectedTaskDelta: number;
};

// Project template types
export type ProjectTemplate = 'wedding' | 'travel' | 'party' | 'renovation' | 'budget' | 'custom';

// Wizard state for onboarding
export type WizardState = {
  completed: boolean;
  currentStep: number;
  dismissedAt?: Timestamp;
  stepsShown: string[];
};

export type BoardData = {
  id: string;
  title: string;
  icon?: string; // Emoji icon for the project
  ownerId?: string;
  users: User[];
  sharedWith?: string[];
  columns: Column[];
  subtaskColumns: Column[];
  sections: Section[];
  labelOptions: {
    [key: string]: StatusOption[];
  };
  access?: {
    [userId: string]: 'owner' | 'contributor' | 'viewer';
  }
  widgets?: Widget[];
  chartCards?: ChartCard[];
  // Bumped by the v2 charts backfill (PR7). When >=2, the doc has been
  // through the migration and chartCards reflects the user's current widget
  // configuration. Distinct from `version` (optimistic locking).
  chartsSchemaVersion?: number;
  // Composed/legacy-root mirror of the active (or tasks) workstream's version.
  // Source of truth is per workstream so migrating one tab cannot make a sibling
  // honor old 6-field-cap flags.
  cardViewSchemaVersion?: number;
  // Bumped when task rows have been backfilled into Firestore subcollections
  // and can be edited independently from the legacy whole-project document.
  syncSchemaVersion?: number;
  // When true, the row documents are the source of truth and a Cloud Function rebuilds
  // the embedded sections/workstreams from them, so clients skip the full-document write.
  rowAuthoritative?: boolean;
  themeColor?: { h: number; s: number; l: number };
  currency?: string; // Currency code (e.g., 'GBP', 'USD', 'EUR')
  hideDoneTasks?: boolean;
  defaultSort?: GlobalSort;
  sectionMetrics?: SectionMetric[]; // Up to 3 metrics to show in section summary
  // When true, a section stays visible even if a filter or search hides every task in it,
  // so empty sections can still be seen, dropped into, renamed and deleted. Project-level:
  // it is a display preference rather than per-workstream data.
  showEmptySections?: boolean;
  tags?: string[];
  tagsGeneratedAt?: Timestamp | string;
  tagsUserEditedAt?: Timestamp | string;
  createdAt?: Timestamp;
  lastEdited?: Timestamp;
  version?: number; // For optimistic locking and conflict detection
  
  // Wizard and setup fields
  setupTemplate?: ProjectTemplate;
  setupType?: 'template' | 'custom' | 'ai'; // Track which setup path was used
  wizardState?: {
    [userId: string]: WizardState;
  };
  
  completed?: boolean; // Marks project as archived/completed
  completedAt?: Timestamp; // When the project was marked complete
  completedBy?: string; // User ID who marked it complete
  lastRestoredAt?: Timestamp;
  lastRestoredBy?: string;
  lastRestoredFromSnapshotId?: string;
  __writeGuard?: ProjectWriteGuard; // Internal client-side write safety metadata. Never persist.
};

export type ProjectSummary = {
  id: string;
  projectId?: string;
  projectID?: string;
  sourceProjectId?: string;
  project?: {
    id?: string;
    projectId?: string;
    projectID?: string;
    sourceProjectId?: string;
    tags?: string[];
    lastEdited?: Timestamp;
    updatedAt?: Timestamp;
    createdAt?: Timestamp;
  };
  title: string;
  icon?: string;
  ownerId: string;
  sharedWith: string[];
  sharedWithCount: number;
  completed: boolean;
  tags: string[];
  hasOverdueTracking: boolean;
  overdueCount: number;
  createdAt?: Timestamp;
  lastEdited?: Timestamp;
};

export interface Invitation {
    id: string;
    inviterId: string;
    inviterName: string;
    inviteeEmail: string;
    projectId: string;
    projectName: string;
    status: 'pending' | 'accepted' | 'declined';
    createdAt: any;
}

// License types
export type LicenseType = 'unpickle_standard' | 'unpickle_pro';

// License metadata
export interface License {
  type: LicenseType;
  validUntil?: Date;
  features: string[];
}

// Feature keys (extendable enum pattern)
export const FEATURES = {
  // Board features
  CUSTOM_WIDGETS: 'custom_widgets',
  DASHBOARD_TAB: 'dashboard_tab',
  ADVANCED_COLUMNS: 'advanced_columns', // Budget, Actual, Currency

  // Collaboration
  UNLIMITED_CONTRIBUTORS: 'unlimited_contributors',
  GUEST_VIEWERS: 'guest_viewers',
  
  // Data
  FILE_ATTACHMENTS: 'file_attachments',
  EXPORT_DATA: 'export_data',
  
  // Customization
  PROJECT_THEMES: 'project_themes',
  CUSTOM_LABELS: 'custom_labels',
  
  // Limits
  UNLIMITED_PROJECTS: 'unlimited_projects',
  UNLIMITED_TASKS: 'unlimited_tasks',
} as const;

export type Feature = typeof FEATURES[keyof typeof FEATURES];

// Notification type for user mentions in comments and due date reminders
export type UserNotification = {
  id: string;
  userId: string; // User who is tagged or assigned
  projectId: string;
  projectName: string;
  taskId: string;
  taskTitle: string;
  sectionId: string;
  timestamp: string;
  read: boolean;
  isSubtask?: boolean;
  parentTaskId?: string;
  
  // Notification type discriminator
  type: 'mention' | 'due_date';
  
  // For mention notifications
  commentId?: string;
  commentContent?: string; // Snippet of the comment
  commentedBy?: User; // User who made the comment
  
  // For due date notifications
  dueDate?: string;
  dueDateStatus?: 'due_today' | 'overdue';
  daysOverdue?: number;
};
